import { useMemo } from "react";
import { useSelector } from "react-redux";

import { selectProfile, selectRole } from "@/redux/selectors/authSelectors";

import { checkPermissionByRole } from "../helpers/checkPermissionByRole";
import type { PermissionKey } from "../types";

type CheckPermissionProps = { permissionKey: PermissionKey };

/**
 * Hook to check if the current user has permission to access a feature.
 * Automatically updates when the user's role or profile changes.
 *
 * @returns `null` if role or profile are not yet loaded, otherwise `boolean`
 */
export function useCheckPermission({ permissionKey }: CheckPermissionProps): boolean | null {
  const role = useSelector(selectRole);
  const profile = useSelector(selectProfile);

  return useMemo(() => {
    // Return null if selectors haven't resolved yet
    if (!role || !profile) return null;

    return checkPermissionByRole({ permissionKey, profile, role });
  }, [permissionKey, profile, role]);
}
